import React from 'react';

import App from '../App';
import { routeSuperset } from './routes';
import { RequireAuth } from '../components/router/RequireAuth';
import { RouteElement } from '../components/router/RouteElement';

/**
 * Turns the nested route config into React Router v7 route objects.
 *
 * Two things make this a near-mechanical translation rather than a rewrite:
 *
 *  - Child paths stay absolute. v7 allows an absolute child path as long as it
 *    extends its parent's path, which every path in this config already does,
 *    so no path string changes.
 *  - v5's `exact` is gone because v7 matches exactly by default. The one case
 *    that needs translating is a child sharing its parent's path (`/portfolio`
 *    inside `/portfolio`), which is precisely what an `index` route is.
 */
const toRouteObject = (route, parentChain = [], parentPath = null) => {
  const chain = [...parentChain, route.path];
  const children = route.routes?.map((child) =>
    toRouteObject(child, chain, route.path)
  );

  const routeObject = { element: <RouteElement chain={chain} /> };

  if (route.path === parentPath) {
    // A child on the same path as its parent is v7's index route.
    routeObject.index = true;
  } else {
    routeObject.path = route.path;
  }

  if (children?.length) {
    routeObject.children = children;
  }

  return routeObject;
};

const publicRoutes = routeSuperset.filter((route) => route.public);
const privateRoutes = routeSuperset.filter((route) => !route.public);

/**
 * `App` is the root layout: it keeps the app-wide chrome and side effects that
 * used to sit around `<Switch>`, and renders the matched route in its `Outlet`.
 */
export const routeTree = [
  {
    element: <App />,
    children: [
      ...publicRoutes.map((route) => toRouteObject(route)),
      {
        element: <RequireAuth />,
        children: privateRoutes.map((route) => toRouteObject(route)),
      },
    ],
  },
];
